import React, { useEffect, useState } from "react";
import { Container, Div, Text } from "../../styles/Common";
import { Input } from "../../components/Input";
import { Button } from "../../components/Button";
import { theme } from "../../styles/theme";
import searchIcon from "../../assets/icons/search.svg";
import useProjectsInfo from "./hooks/UseProjectsInfo";
import useChangeProjectStatus from "./hooks/useChangeProjectStatus";
import useDebounce from "../../core/hooks/useDebounce";
import { Table } from "../../components/Table";
import { Skeleton } from "../../components/Skeleton";
import { Link } from "../../components/Link";
import { Select } from "../../components/Select";
import show from "../../assets/icons/show.svg";
import showDisabled from "../../assets/icons/show-disabled.svg";
import edit from "../../assets/icons/edit.svg";
import editDisabled from "../../assets/icons/edit-disabled.svg";
import file from "../../assets/icons/file-text.svg";
import fileDisabled from "../../assets/icons/file-disabled.svg";
import checkCircle from "../../assets/icons/check-circle.svg";
import { useNavigate } from "react-router-dom";
import { getUserPermissions, hasPermission } from "../../utilities/permissions";
import { getUserInfo } from "../../utilities/helpers";
import { WrapperStatus } from "../UserManagement/styles";
import toast from "react-hot-toast";

export const Projects = () => {
  const navigate = useNavigate();
  const { role } = getUserInfo();

  const changeStatus = useChangeProjectStatus();

  const statusOptions = [
    { value: 0, label: "Pending" },
    { value: 1, label: "Active" },
    { value: 2, label: "Completed" },
  ];

  const getBackgroundStatus = (status) => {
    switch (status) {
      case "Active":
        return theme.colors.greenLight;
      case "Completed":
        return theme.colors.salmon1;
      case "Pending":
        return theme.colors.yellowLight;
      default:
        return theme.colors.grey3;
    }
  };

  const getColorStatus = (status) => {
    switch (status) {
      case "Active":
        return theme.colors.green;
      case "Completed":
        return theme.colors.salmon;
      case "Pending":
        return theme.colors.yellow;
      default:
        return theme.colors.grey1;
    }
  };

  const canChangeStatus = () => {
    if (role === "superAdmin") return true;
    return Object.values(getUserPermissions()).some(
      (perm) => perm.role === "Admin" || perm.role === "Chief Inspector"
    );
  };

  const [forceUpdate, setForceUpdate] = useState(false);
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [dataProjects, setDataProjects] = useState([]);
  const [totalItems, setTotalItems] = useState(10);
  const [perPage, setPerPage] = useState(10);
  const [pages, setPages] = useState({ current: 1, pageSize: 10 });
  const [searchBy, setSearchBy] = useState("");
  const searchValue = useDebounce(searchBy, 600);
  const getProjects = useProjectsInfo(pages.current, perPage, searchValue);
  const { data } = getProjects;
  const { data: dataProjectsList, isLoading: loading, refetch: refetchProjects } = data;

  const handleDetail = (id, projectName, projectAFE) => {
    navigate(
      `/projects/${id}?projectAFE=${projectAFE}&projectName=${projectName}`
    );
  };

  const handleDocuments = (id, projectAFE, projectName) => {
    navigate(
      `/documents/${id}?projectAFE=${projectAFE}&projectName=${projectName}`
    );
  };

  const handleUpdate = (id) => {
    navigate(`/projects/update-project/${id}`);
  };

  useEffect(() => {
    if (dataProjectsList?.data?.projects?.items.length > 0) {
      const dataTable = dataProjectsList.data.projects.items.map((ele, i) => ({
        ...ele,
        key: `${ele.id}`,
        actions: [
          {
            key: `1${i}`,
            id: ele.id,
            name: "Details",
            icon: hasPermission(ele.id, "View Line") ? show : showDisabled,
            onClick: () =>
              handleDetail(ele.id, ele.projectName, ele.projectAFE),
            permission: hasPermission(ele.id, "View Line"),
          },
          {
            key: `2${i}`,
            id: ele.id,
            name: "Edit",
            icon: (hasPermission(ele.id, "Edit Project") || hasPermission(ele.id, "Assign User")) ? edit : editDisabled,
            onClick: () => handleUpdate(ele.id),
            permission: hasPermission(ele.id, "Edit Project") || hasPermission(ele.id, "Assign User"),
          },
          {
            key: `1${i}`,
            id: ele.id,
            name: "Documents",
            icon: hasPermission(ele.id, "View Documents") ? file : fileDisabled,
            onClick: () =>
              handleDocuments(ele.id, ele.projectAFE, ele.projectName),
            permission: hasPermission(ele.id, "View Documents"),
          },
        ],
      }));

      setDataProjects(dataTable);
      setTotalItems(dataProjectsList?.data?.projects.totalItems);
    } else {
      setDataProjects([]);
    }
  }, [dataProjectsList?.data]);

  const tableColumns = [
    {
      title: "Company Name",
      dataIndex: "companyName",
      key: "companyName",
      width: 150,
      render: (text) => <div style={{ width: "150px" }}>{text}</div>,
    },
    {
      title: "Owner Company",
      dataIndex: "ownerCompany",
      key: "ownerCompany",
      width: 150,
      render: (text) => <div style={{ width: "150px" }}>{text}</div>,
    },
    {
      title: "Project AFE",
      dataIndex: "projectAFE",
      key: "projectAFE",
      width: 120,
      render: (text) => <div style={{ width: "120px" }}>{text}</div>,
    },
    {
      title: "Project Name",
      dataIndex: "projectName",
      key: "projectName",
      width: 150,
      render: (text) => <div style={{ width: "150px" }}>{text}</div>,
    },
    ...(role !== "superAdmin" ? [{
      title: "Roles",
      dataIndex: "roles",
      key: "roles",
      width: 150,
      render: (roles) => (
        <div style={{ width: "150px" }}>
          {roles ? roles.join(", ") : ""}
        </div>
      ),
    }] : []),
    {
      title: "Status",
      dataIndex: "statusName",
      key: "statusName",
      width: 180,
      render: (statusName, record) => {
        const isEditing = editingStatusId === record.id;

        if (isEditing) {
          return (
            <Div align="center" gap="8px">
              <Select
                options={statusOptions}
                value={selectedStatus}
                onChange={(value) => setSelectedStatus(value)}
                placeholder="Select status"
                height="36px"
                style={{ width: "130px" }}
              />
              <img
                alt="save"
                src={checkCircle}
                style={{ cursor: "pointer", width: "20px", height: "20px" }}
                onClick={() => handleSaveStatus(record.id)}
              />
              <img
                alt="cancel"
                src={require("../../assets/icons/close.svg").default}
                style={{ cursor: "pointer", width: "16px", height: "16px" }}
                onClick={handleCancelEditStatus}
              />
            </Div>
          );
        }

        return (
          <Div align="center" gap="8px">
            <WrapperStatus
              background={getBackgroundStatus(statusName)}
              color={getColorStatus(statusName)}
            >
              {statusName}
            </WrapperStatus>
            {canChangeStatus() && (
              <img
                alt="edit status"
                src={edit}
                style={{ cursor: "pointer", width: "16px", height: "16px" }}
                onClick={() => handleEditStatus(record.id, statusName)}
              />
            )}
          </Div>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      align: "center",
      width: 180,
      render: (actions) => (
        <Div gap="60px">
          {actions.map((action, i) => (
            <Link
              key={i}
              onClick={action.onClick}
              color={theme.colors.salmon}
              icon={
                <img
                  alt={action.name}
                  src={action.icon}
                  style={{ marginRight: "16px" }}
                />
              }
              disabled={!action.permission}
              styles={{
                "&:hover": {
                  background: "transparent",
                },
              }}
            >
              {action.name}
            </Link>
          ))}
        </Div>
      ),
    },
  ];

  const handleEditStatus = (projectId, currentStatus) => {
    setEditingStatusId(projectId);
    const currentOption = statusOptions.find((opt) => opt.label === currentStatus);
    setSelectedStatus(currentOption ? currentOption.value : null);
  };

  const handleCancelEditStatus = () => {
    setEditingStatusId(null);
    setSelectedStatus(null);
  };

  const handleSaveStatus = (projectId) => {
    if (selectedStatus === null) return;
    changeStatus.reset();
    changeStatus.mutate(
      { projectId, status: selectedStatus },
      {
        onSuccess: () => {
          toast.success("Project status updated successfully");
          setEditingStatusId(null);
          setSelectedStatus(null);
          refetchProjects();
        },
        onError: (err) => {
          toast.error(err?.response?.data?.Errors?.[0] || "Error updating status");
        },
      }
    );
  };

  const handleNewProject = () => {
    navigate("/projects/create-project");
  };

  const handleChangeTable = (pagination) => {
    setPages(pagination);
    setPerPage(pagination.pageSize);
    setForceUpdate(!forceUpdate);
  };

  const handleChangeSearch = (event) => {
    const { value } = event.target;
    setPages({ current: 1, pageSize: 10 });
    setSearchBy(value);
    setForceUpdate(!forceUpdate);
  };

  const hasAdmin = Object.values(getUserPermissions()).some(role => role.role === "Admin");
  const canCreateProject = role === "superAdmin" || hasAdmin;

  return (
    <Container>
      <Div width="100%" height="55px" justify="space-between" align="center">
        <Div direction="column">
          <Text
            size={theme.fonts.size.h4}
            color={theme.colors.dark}
            weight={theme.fonts.weight.bold}
          >
            Your Database
          </Text>
        </Div>
        <Div gap="24px">
          <Input
            placeholder="Search..."
            suffix={<img alt="search" src={searchIcon} />}
            width="324px"
            onChange={handleChangeSearch}
          />
          {canCreateProject && (
            <Button background={theme.colors.salmon} onClick={handleNewProject}>
              New Project
            </Button>
          )}
        </Div>
      </Div>
      <Div m="32px 0 0 0">
        <Table
          columns={tableColumns}
          data={dataProjects}
          rowClassName={(record, index) =>
            index % 2 === 0 ? "table-row-light" : "table-row-dark"
          }
          sizerLabels={["Show", "Results"]}
          pagination={{
            defaultCurrent: 1,
            ...pages,
            total: totalItems,
            showSizeChanger: true,
            pageSizeOptions: ["10", "50", "100"],
            locale: {
              items_per_page: "",
            },
            position: ["bottomCenter"],
          }}
          onChange={handleChangeTable}
          locale={{
            emptyText: loading ? (
              <Div justify="center" gap="16px">
                {[...Array(9)].map((v, idx) => (
                  <Skeleton
                    title={false}
                    paragraph={{
                      rows: 10,
                      width: "100%",
                    }}
                    loading
                    key={idx}
                  />
                ))}
              </Div>
            ) : (
              "No data"
            ),
          }}
        />
      </Div>
    </Container>
  );
};
